import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Rating,
  LinearProgress,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  RateReview as ReviewIcon
} from '@mui/icons-material';
import { getFirestore, collection, query, where, getDocs } from 'firebase/firestore';
import { DEFAULT_AVERAGE_RATING } from '../utils/ratingUtils';

interface RatingStats {
  averageRating: number;
  totalReviews: number;
  ratingDistribution: {
    5: number;
    4: number;
    3: number;
    2: number;
    1: number;
  };
  recentReviews: Array<{
    id: string;
    reviewerName: string;
    reviewerAvatar?: string;
    rating: number;
    comment: string;
    date: string;
    rentalId: string;
    dressName: string;
  }>;
  badges: Array<{
    name: string;
    description: string;
    earned: boolean;
  }>;
}

interface UserRatingStatsProps {
  userId: string;
  userType: 'renter' | 'owner';
  title?: string;
}

const UserRatingStats: React.FC<UserRatingStatsProps> = ({ userId, userType, title }) => {
  const [stats, setStats] = useState<RatingStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadRatingStats();
  }, [userId, userType]);

  const loadRatingStats = async () => {
    try {
      // Charger les vraies données depuis Firestore
      const db = getFirestore();
      const reviewsQuery = query(
        collection(db, 'reviews'),
        where('reviewedUserId', '==', userId)
      );
      const reviewsSnapshot = await getDocs(reviewsQuery);
      
      const reviews: any[] = [];
      const ratingDistribution = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
      let totalRating = 0;
      
      reviewsSnapshot.forEach((doc) => {
        const reviewData = doc.data();
        const rating = reviewData.rating || 0;
        reviews.push({
          id: doc.id,
          ...reviewData
        });
        ratingDistribution[rating as keyof typeof ratingDistribution]++;
        totalRating += rating;
      });
      
      const totalReviews = reviews.length;
      const averageRating = totalReviews > 0 ? parseFloat((totalRating / totalReviews).toFixed(1)) : 0;
      
      // Trier les avis par date (plus récents en premier)
      const recentReviews = reviews
        .sort((a, b) => {
          const dateA = a.date?.toDate ? a.date.toDate() : new Date(a.date);
          const dateB = b.date?.toDate ? b.date.toDate() : new Date(b.date);
          return dateB.getTime() - dateA.getTime();
        })
        .slice(0, 10)
        .map((review) => ({
          id: review.id,
          reviewerName: review.reviewerName || 'Utilisateur anonyme',
          reviewerAvatar: review.reviewerAvatar || '',
          rating: review.rating,
          comment: review.comment || '',
          date: review.date?.toDate ? review.date.toDate().toISOString().split('T')[0] : review.date,
          rentalId: review.rentalId,
          dressName: review.dressName || ''
        }));
      
      // Calculer les badges (logique simplifiée)
      const badges = [
        {
          name: 'Super Hôte',
          description: 'Plus de 10 avis positifs',
          earned: totalReviews >= 10 && averageRating >= 4.5
        },
        {
          name: 'Ponctuel',
          description: 'Jamais en retard',
          earned: false // À calculer depuis les données de location
        },
        {
          name: 'Soigneux',
          description: 'Vêtements toujours en parfait état',
          earned: false // À calculer depuis les données de dommages
        },
        {
          name: 'Communicatif',
          description: 'Répond rapidement aux messages',
          earned: false // À calculer depuis les données de messages
        },
        {
          name: 'Vétéran',
          description: 'Plus de 50 locations',
          earned: false // À calculer depuis les données de locations
        }
      ];

      setStats({
        averageRating: averageRating || DEFAULT_AVERAGE_RATING,
        totalReviews,
        ratingDistribution,
        recentReviews,
        badges
      });
    } catch (error) {
      console.error('Erreur lors du chargement des statistiques:', error);
      // En cas d'erreur, afficher des stats vides
      setStats({
        averageRating: DEFAULT_AVERAGE_RATING,
        totalReviews: 0,
        ratingDistribution: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 },
        recentReviews: [],
        badges: []
      });
    } finally {
      setLoading(false);
    }
  };

  const getRatingPercentage = (rating: number) => {
    if (!stats) return 0;
    const total = Object.values(stats.ratingDistribution).reduce((sum, count) => sum + count, 0);
    return total > 0 ? (stats.ratingDistribution[rating as keyof typeof stats.ratingDistribution] / total) * 100 : 0;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <Typography>Chargement des statistiques...</Typography>
      </Box>
    );
  }

  if (!stats) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Aucune donnée disponible.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 4, color: '#C9B6AB' }}>
        {title || 'Mes notes et avis'}
      </Typography>

      <Grid container spacing={3}>
        {/* Note moyenne */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              {stats.totalReviews > 0 ? (
                <>
                  <Typography variant="h2" sx={{ color: '#C9B6AB', fontWeight: 'bold', mb: 1 }}>
                    {stats.averageRating}
                  </Typography>
                  <Rating value={stats.averageRating} readOnly size="large" />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Basé sur {stats.totalReviews} avis
                  </Typography>
                </>
              ) : (
                <>
                  <Typography variant="h6" sx={{ color: '#C9B6AB', fontWeight: 'bold', mb: 2 }}>
                    Aucun avis pour le moment
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Les avis apparaîtront ici une fois que vous recevrez vos premiers retours.
                  </Typography>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Distribution des notes */}
        <Grid item xs={12} md={8}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Distribution des notes
              </Typography>
              {stats.totalReviews > 0 ? (
                [5, 4, 3, 2, 1].map((rating) => (
                <Box key={rating} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" sx={{ minWidth: 20, mr: 1 }}>
                    {rating}
                  </Typography>
                  <Rating 
                    value={rating} 
                    readOnly 
                    size="small"
                    sx={{ 
                      mr: 1,
                      '& .MuiRating-iconFilled': {
                        color: '#C9B6AB'
                      },
                      '& .MuiRating-iconEmpty': {
                        color: '#e0e0e0'
                      }
                    }}
                  />
                  <LinearProgress
                    variant="determinate"
                    value={getRatingPercentage(rating)}
                    sx={{ 
                      flexGrow: 1, 
                      mr: 1,
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: '#C9B6AB'
                      }
                    }}
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 30 }}>
                    {stats.ratingDistribution[rating as keyof typeof stats.ratingDistribution]}
                  </Typography>
                </Box>
              ))
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    Aucune donnée de distribution disponible
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Badges */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <TrendingUpIcon sx={{ mr: 1, color: '#C9B6AB' }} />
                Badges et récompenses
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {stats.badges.map((badge, index) => (
                  <Chip
                    key={index}
                    label={badge.name}
                    color={badge.earned ? 'primary' : 'default'}
                    variant={badge.earned ? 'filled' : 'outlined'}
                    sx={{
                      backgroundColor: badge.earned ? '#C9B6AB' : 'transparent',
                      color: badge.earned ? 'white' : '#C9B6AB',
                      borderColor: '#C9B6AB',
                      '&:hover': {
                        backgroundColor: badge.earned ? '#C1A08E' : 'rgba(201, 182, 171, 0.1)',
                      }
                    }}
                  />
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Avis récents */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <ReviewIcon sx={{ mr: 1, color: '#C9B6AB' }} />
                Avis récents
              </Typography>
              <List>
                {stats.recentReviews.map((review, index) => (
                  <React.Fragment key={review.id}>
                    <ListItem alignItems="flex-start">
                      <ListItemAvatar>
                        <Avatar src={review.reviewerAvatar} />
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Typography variant="subtitle2" sx={{ mr: 1 }}>
                              {review.reviewerName}
                            </Typography>
                            <Rating value={review.rating} readOnly size="small" />
                            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                              {new Date(review.date).toLocaleDateString('fr-FR')}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.primary" sx={{ mb: 1 }}>
                              {review.comment}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Location: {review.dressName}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < stats.recentReviews.length - 1 && <Divider variant="inset" component="li" />}
                  </React.Fragment>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default UserRatingStats;
